import { useState, useMemo } from 'react';
import {
  ChevronLeft, ChevronRight, AlertCircle, Clock, CheckCircle2, XCircle,
  Briefcase, Plus, Trash2, Timer,
} from 'lucide-react';
import { Card, CardContent } from '@travellegacy/nexops-design-system';
import { Select } from '@travellegacy/nexops-design-system';
import {
  Table,
  SortableTable,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@travellegacy/nexops-design-system';
import { cn } from '@/shared/lib/utils';
import { useTimesheet, useTimeEntries } from '../hooks/useTimeEntry';
import { useAdjustmentRequests, useAdjustmentRequestMutations } from '../hooks/useAdjustmentRequests';
import { useTimeAllocations, useTimeAllocationMutations, useProjects } from '../hooks/useProjects';
import { useEmployees, useMyProfile } from '../hooks/useEmployees';
import { useAuth } from '@/shared/contexts/AuthContext';
import {
  formatHours,
  formatDateShort,
  formatTimeBRT,
  toDateString,
  type Timesheet,
  type TimeEntry,
  type TimeAdjustmentRequest,
  type TimeAllocation,
  type AdjustmentStatus,
  type Project,
} from '../types';

// ==========================================
// Badge de saldo
// ==========================================
function BalanceBadge({ hours }: { hours: number }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium',
        hours > 0
          ? 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400'
          : hours < 0
            ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400'
            : 'bg-muted text-muted-foreground'
      )}
    >
      {hours >= 0 ? '+' : ''}{formatHours(hours)}
    </span>
  );
}

// ==========================================
// Badge de status de ajuste
// ==========================================
const adjustmentStatusConfig: Record<AdjustmentStatus, {
  icon: React.ElementType;
  label: string;
  className: string;
}> = {
  PENDING: {
    icon: Clock,
    label: 'Ajuste pendente',
    className: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
  },
  APPROVED: {
    icon: CheckCircle2,
    label: 'Ajuste aprovado',
    className: 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400',
  },
  REJECTED: {
    icon: XCircle,
    label: 'Ajuste reprovado',
    className: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400',
  },
};

function AdjustmentStatusBadge({ status }: { status: AdjustmentStatus }) {
  const cfg = adjustmentStatusConfig[status];
  const Icon = cfg.icon;
  return (
    <span className={cn(
      'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium whitespace-nowrap',
      cfg.className
    )}>
      <Icon className="h-3 w-3 shrink-0" />
      {cfg.label}
    </span>
  );
}

// ==========================================
// Badge de horas lançadas
// ==========================================
function HoursBadge({ launched, expected }: { launched: number; expected: number }) {
  if (launched === 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium whitespace-nowrap bg-muted text-muted-foreground">
        <Timer className="h-3 w-3 shrink-0" />
        Sem lançamento
      </span>
    );
  }
  if (launched >= expected - 0.01) {
    return (
      <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium whitespace-nowrap bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400">
        <CheckCircle2 className="h-3 w-3 shrink-0" />
        {formatHours(launched)} lançado
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium whitespace-nowrap bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400">
      <Timer className="h-3 w-3 shrink-0" />
      {formatHours(launched)} / {formatHours(expected)}
    </span>
  );
}

// ==========================================
// Modal de lançamento de horas
// ==========================================
interface HoursModalProps {
  row: Timesheet;
  allocations: TimeAllocation[];
  projects: Project[];
  dateStr: string;
  employeeId?: string; // Opcional: usado por admin/gestor para lançar horas de outros colaboradores
  onClose: () => void;
}

function HoursModal({ row, allocations, projects, dateStr, employeeId, onClose }: HoursModalProps) {
  const { create, remove } = useTimeAllocationMutations();

  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [selectedActivityId, setSelectedActivityId] = useState('');
  const [duration, setDuration] = useState(''); // formato HH:MM
  const [description, setDescription] = useState('');

  // Converte "HH:MM" para horas decimais (ex: "00:50" → 0.8333)
  const durationToHours = (val: string): number | null => {
    const [h, m] = val.split(':').map(Number);
    if (isNaN(h) || isNaN(m) || m > 59) return null;
    return h + m / 60;
  };

  const hoursValue = durationToHours(duration);

  // Projetos ativos com atividades
  const activeProjects = projects.filter((p) => p.isActive && (p.activities ?? []).some((a) => a.isActive));

  // Atividades do projeto selecionado
  const activities = useMemo(() => {
    const proj = activeProjects.find((p) => p.id === selectedProjectId);
    return (proj?.activities ?? []).filter((a) => a.isActive);
  }, [selectedProjectId, activeProjects]);

  // Total já lançado no dia
  const totalLaunched = allocations.reduce((sum, a) => sum + a.hours, 0);

  // Horas disponíveis para lançamento (não pode ultrapassar horas trabalhadas)
  const availableHours = Math.max(0, row.hoursWorked - totalLaunched);
  const canLaunch = availableHours > 0;

  // Tolerância de 1 minuto (0.0167 horas) para evitar problemas de arredondamento
  const TOLERANCE = 0.0167;
  const wouldExceed = hoursValue !== null && hoursValue > (availableHours + TOLERANCE);

  const handleProjectChange = (id: string) => {
    setSelectedProjectId(id);
    setSelectedActivityId('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedActivityId || hoursValue === null || hoursValue <= 0) return;

    // Validação: não pode lançar mais do que o disponível (com tolerância)
    if (hoursValue > (availableHours + TOLERANCE)) {
      return; // O botão já está desabilitado, mas garante que não submete
    }

    await create.mutateAsync({
      activityId: selectedActivityId,
      date: dateStr,
      hours: hoursValue,
      description: description.trim() || undefined,
      employeeId, // Envia o employeeId quando admin/gestor está lançando para outro colaborador
    });
    setSelectedProjectId('');
    setSelectedActivityId('');
    setDuration('');
    setDescription('');
  };

  const selectClass = cn(
    'w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm',
    'focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50'
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl shadow-xl border w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">

          {/* Header */}
          <div className="flex items-start justify-between mb-5">
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-blue-600" />
                Lançar Horas
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">{formatDateShort(row.date)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Lançado / Trabalhado</p>
              <p className={cn(
                'text-sm font-semibold font-mono',
                totalLaunched >= row.hoursWorked ? 'text-blue-600 dark:text-blue-400' : 'text-orange-600 dark:text-orange-400'
              )}>
                {formatHours(totalLaunched)} / {formatHours(row.hoursWorked)}
              </p>
              <p className={cn(
                'text-xs font-medium mt-0.5',
                canLaunch ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
              )}>
                {canLaunch ? `Disponível: ${formatHours(availableHours)}` : 'Sem horas disponíveis'}
              </p>
            </div>
          </div>

          {/* Alerta se não houver horas disponíveis */}
          {!canLaunch && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900 p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-400">
                Você já lançou todas as horas trabalhadas neste dia ({formatHours(row.hoursWorked)}).
                Para lançar mais horas, remova lançamentos existentes ou solicite um ajuste de ponto.
              </p>
            </div>
          )}

          {/* Formulário */}
          <form onSubmit={handleSubmit} className="space-y-3 mb-5">
            {/* Projeto */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Projeto</label>
              <select
                value={selectedProjectId}
                onChange={(e) => handleProjectChange(e.target.value)}
                className={selectClass}
                required
                disabled={!canLaunch}
              >
                <option value="">
                  {canLaunch ? 'Selecione um projeto...' : 'Sem horas disponíveis'}
                </option>
                {activeProjects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* Atividade */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Atividade</label>
              <select
                value={selectedActivityId}
                onChange={(e) => setSelectedActivityId(e.target.value)}
                className={selectClass}
                required
                disabled={!selectedProjectId || !canLaunch}
              >
                <option value="">
                  {!canLaunch
                    ? 'Sem horas disponíveis'
                    : selectedProjectId
                      ? 'Selecione uma atividade...'
                      : 'Selecione um projeto primeiro'}
                </option>
                {activities.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>

            {/* Duração + Descrição */}
            <div className="flex gap-3">
              <div className="w-28 shrink-0">
                <label className="block text-xs font-medium text-muted-foreground mb-1">Duração</label>
                <input
                  type="time"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  required
                  disabled={!canLaunch}
                  className={cn(
                    'w-full rounded-md border bg-background px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2',
                    wouldExceed ? 'border-red-500 ring-red-500' : 'border-input focus:ring-ring',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                />
                {wouldExceed && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    Excede disponível
                  </p>
                )}
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-muted-foreground mb-1">Descrição <span className="text-muted-foreground/60">(opcional)</span></label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="O que foi feito..."
                  disabled={!canLaunch}
                  className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
            </div>

            {/* Ações */}
            <div className="flex gap-3 pt-1">
              <button
                type="submit"
                disabled={
                  create.isPending ||
                  !selectedActivityId ||
                  !hoursValue ||
                  hoursValue <= 0 ||
                  wouldExceed ||
                  !canLaunch
                }
                className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                <Plus className="h-4 w-4" />
                {create.isPending
                  ? 'Salvando...'
                  : wouldExceed
                    ? 'Excede horas disponíveis'
                    : !canLaunch
                      ? 'Sem horas disponíveis'
                      : 'Salvar lançamento'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-sm font-medium rounded-lg border hover:bg-accent transition-colors"
              >
                Fechar
              </button>
            </div>
          </form>

          {/* Lista de lançamentos do dia */}
          {allocations.length > 0 ? (
            <div className="rounded-lg border overflow-hidden">
              <div className="bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
                Lançamentos do dia
              </div>
              <div className="divide-y">
                {allocations.map((alloc) => (
                  <div key={alloc.id} className="flex items-center justify-between px-3 py-2.5 gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {alloc.activity?.project?.name ?? '—'}
                        <span className="text-muted-foreground font-normal"> / {alloc.activity?.name ?? '—'}</span>
                      </p>
                      {alloc.description && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{alloc.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="font-mono text-sm font-semibold text-blue-600 dark:text-blue-400">
                        {formatHours(alloc.hours)}
                      </span>
                      <button
                        onClick={() => remove.mutate(alloc.id)}
                        disabled={remove.isPending}
                        className="text-muted-foreground hover:text-red-500 transition-colors disabled:opacity-40"
                        title="Remover lançamento"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-muted/20 px-3 py-2 flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Total lançado</span>
                <span className="font-mono text-sm font-semibold">{formatHours(totalLaunched)}</span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4 rounded-lg border border-dashed">
              Nenhum lançamento para este dia ainda.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ==========================================
// Modal de solicitação de ajuste
// ==========================================
interface AdjustmentModalProps {
  row: Timesheet;
  entry: TimeEntry | undefined;
  onClose: () => void;
}

function AdjustmentModal({ row, entry, onClose }: AdjustmentModalProps) {
  const { create } = useAdjustmentRequestMutations();

  const [form, setForm] = useState({
    clockIn: '',
    breakStart: '',
    breakEnd: '',
    clockOut: '',
    justification: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const requestedData: { clockIn?: string; clockOut?: string; breakStart?: string; breakEnd?: string } = {};
    if (form.clockIn) requestedData.clockIn = form.clockIn;
    if (form.breakStart) requestedData.breakStart = form.breakStart;
    if (form.breakEnd) requestedData.breakEnd = form.breakEnd;
    if (form.clockOut) requestedData.clockOut = form.clockOut;

    const dateStr = toDateString(row.date);

    await create.mutateAsync({
      date: dateStr,
      requestedData,
      justification: form.justification,
    });
    onClose();
  };

  const inputClass =
    'w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary';

  const fields: { label: string; current: string | null | undefined; key: keyof typeof form }[] = [
    { label: 'Entrada',       current: entry?.clockIn,    key: 'clockIn' },
    { label: 'Saída Almoço',  current: entry?.breakStart, key: 'breakStart' },
    { label: 'Volta Almoço',  current: entry?.breakEnd,   key: 'breakEnd' },
    { label: 'Saída',         current: entry?.clockOut,   key: 'clockOut' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-xl shadow-xl border w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-1">Solicitar Ajuste de Ponto</h3>
          <p className="text-sm text-muted-foreground mb-5">
            {formatDateShort(row.date)} — informe os horários corretos e a justificativa
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left py-2 px-3 font-medium text-muted-foreground">Campo</th>
                    <th className="text-center py-2 px-3 font-medium text-muted-foreground">Atual</th>
                    <th className="text-center py-2 px-3 font-medium text-muted-foreground">Corrigir para</th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((f) => (
                    <tr key={f.key} className="border-t">
                      <td className="py-2 px-3 text-muted-foreground font-medium">{f.label}</td>
                      <td className="py-2 px-3 text-center font-mono text-foreground">
                        {formatTimeBRT(f.current) !== '--:--' ? (
                          <span>{formatTimeBRT(f.current)}</span>
                        ) : (
                          <span className="text-muted-foreground text-xs">não registrado</span>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        <input
                          type="time"
                          value={form[f.key]}
                          onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                          className="w-full rounded border bg-background px-2 py-1 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Justificativa <span className="text-red-500">*</span>
              </label>
              <textarea
                required
                rows={3}
                value={form.justification}
                onChange={(e) => setForm((p) => ({ ...p, justification: e.target.value }))}
                placeholder="Descreva o motivo do ajuste..."
                className={`${inputClass} resize-none`}
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={create.isPending}
                className="flex-1 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                {create.isPending ? 'Enviando...' : 'Enviar Solicitação'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 text-sm font-medium rounded-lg border hover:bg-accent transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// Painel de totais
// ==========================================
interface TotalsBarProps {
  worked: number;
  expected: number;
  balance: number;
}

function TotalsBar({ worked, expected, balance }: TotalsBarProps) {
  return (
    <div className="grid grid-cols-3 gap-4 text-center rounded-lg border bg-muted/20 px-4 py-3">
      <div>
        <p className="text-xs text-muted-foreground">Total Trabalhado</p>
        <p className="font-mono font-semibold text-sm mt-0.5">{formatHours(worked)}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Total Esperado</p>
        <p className="font-mono font-semibold text-sm text-muted-foreground mt-0.5">{formatHours(expected)}</p>
      </div>
      <div>
        <p className="text-xs text-muted-foreground">Saldo do Período</p>
        <p className={cn(
          'font-mono font-semibold text-sm mt-0.5',
          balance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
        )}>
          {balance >= 0 ? '+' : ''}{formatHours(balance)}
        </p>
      </div>
    </div>
  );
}

// ==========================================
// Props
// ==========================================
interface TimesheetTableProps {
  employeeId?: string;
  startDate: string;
  endDate: string;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  monthLabel: string;
}

// ==========================================
// TimesheetTable
// ==========================================
export function TimesheetTable({ startDate, endDate, onPrevMonth, onNextMonth, monthLabel }: TimesheetTableProps) {
  const { user } = useAuth();
  const canSelectEmployee = user?.role === 'ADMIN' || user?.role === 'GESTOR';

  // Admin/Gestor: busca lista completa de employees
  // Colaborador: busca apenas o próprio perfil
  const { data: employees } = useEmployees();
  const { data: myProfile } = useMyProfile();
  const { data: projects } = useProjects();

  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | undefined>(undefined);
  const effectiveEmployeeId = canSelectEmployee ? selectedEmployeeId : undefined;

  // Se for admin/gestor e não selecionou colaborador, não carregar dados
  const shouldLoadData = !canSelectEmployee || selectedEmployeeId !== undefined;

  const { data: timesheet, isLoading } = useTimesheet(
    { startDate, endDate, employeeId: effectiveEmployeeId },
    { enabled: shouldLoadData }
  );
  const { data: entries } = useTimeEntries(
    { startDate, endDate, employeeId: effectiveEmployeeId },
    { enabled: shouldLoadData }
  );
  const { data: adjustmentRequests } = useAdjustmentRequests(
    { startDate, endDate, employeeId: effectiveEmployeeId },
    { enabled: shouldLoadData }
  );
  const { data: allocations } = useTimeAllocations(
    { startDate, endDate, employeeId: effectiveEmployeeId },
    { enabled: shouldLoadData }
  );

  const [adjustingRow, setAdjustingRow] = useState<Timesheet | null>(null);
  const [hoursRow, setHoursRow] = useState<Timesheet | null>(null);

  // Mapeia entries por data YYYY-MM-DD
  const entriesByDate = (entries ?? []).reduce<Record<string, TimeEntry>>((acc, e) => {
    acc[toDateString(e.date)] = e;
    return acc;
  }, {});

  // Mapeia ajustes por data — prioriza APPROVED > PENDING > REJECTED
  const adjustmentsByDate = (adjustmentRequests ?? []).reduce<Record<string, TimeAdjustmentRequest>>((acc, r) => {
    const key = toDateString(r.date);
    const existing = acc[key];
    if (!existing) {
      acc[key] = r;
    } else {
      const priority: Record<AdjustmentStatus, number> = { APPROVED: 3, PENDING: 2, REJECTED: 1 };
      if (priority[r.status] > priority[existing.status]) acc[key] = r;
    }
    return acc;
  }, {});

  // Mapeia lançamentos por data
  const allocationsByDate = (allocations ?? []).reduce<Record<string, TimeAllocation[]>>((acc, a) => {
    const key = toDateString(a.date);
    if (!acc[key]) acc[key] = [];
    acc[key].push(a);
    return acc;
  }, {});

  // Enriquece cada linha com horários do TimeEntry, ajuste e lançamentos
  type TimesheetRow = Timesheet & {
    clockIn: string | null;
    breakStart: string | null;
    breakEnd: string | null;
    clockOut: string | null;
    adjustment: TimeAdjustmentRequest | undefined;
    dayAllocations: TimeAllocation[];
    totalLaunched: number;
  };

  /**
   * Gera todas as datas entre startDate e endDate (inclusivo)
   */
  const generateDateRange = (start: string, end: string): string[] => {
    const dates: string[] = [];
    const current = new Date(start + 'T00:00:00.000Z');
    const endDate = new Date(end + 'T00:00:00.000Z');

    while (current <= endDate) {
      const year = current.getUTCFullYear();
      const month = String(current.getUTCMonth() + 1).padStart(2, '0');
      const day = String(current.getUTCDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
      current.setUTCDate(current.getUTCDate() + 1);
    }

    return dates;
  };

  /**
   * Verifica se uma data é final de semana (sábado=6 ou domingo=0)
   */
  const isWeekend = (dateStr: string): boolean => {
    const date = new Date(dateStr + 'T00:00:00.000Z');
    const dayOfWeek = date.getUTCDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  // Buscar o colaborador para pegar dailyWorkHours
  // Se for admin/gestor: busca o colaborador selecionado na lista
  // Se for colaborador: usa o próprio perfil (myProfile)
  const selectedEmployee = canSelectEmployee
    ? employees?.find((e) => e.id === selectedEmployeeId)
    : myProfile;
  const defaultExpectedHours = selectedEmployee?.dailyWorkHours ?? 8;

  // Criar um mapa de timesheet por data para facilitar a busca
  const timesheetByDate = (timesheet ?? []).reduce<Record<string, Timesheet>>((acc, t) => {
    acc[toDateString(t.date)] = t;
    return acc;
  }, {});

  // Gerar todas as datas do período
  const allDates = generateDateRange(startDate, endDate);

  // Criar linhas completas: reais + fake
  const completeRows: Timesheet[] = allDates.map((dateStr) => {
    const existingRow = timesheetByDate[dateStr];

    // Se existe um registro real, usa ele
    if (existingRow) {
      return existingRow;
    }

    // Se é final de semana e não há registro, pula (não cria linha fake)
    if (isWeekend(dateStr)) {
      return null;
    }

    // Se não tem employee identificado, não pode criar linha fake
    if (!selectedEmployee?.id) {
      return null;
    }

    // Criar linha fake para dia sem registro
    const fakeId = `fake-${dateStr}`;
    const fakeDate = dateStr + 'T00:00:00.000Z';
    const fakeRow: Timesheet = {
      id: fakeId,
      employeeId: selectedEmployee.id,
      date: fakeDate,
      hoursWorked: 0,
      expectedHours: defaultExpectedHours,
      hourBalance: -defaultExpectedHours,
      accumulatedBalance: 0, // Será recalculado se necessário
      approvedBalance: 0,
      pendingAdjustmentsHours: 0,
      entries: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    return fakeRow;
  }).filter((row): row is Timesheet => row !== null);

  // Enriquecer as linhas com dados adicionais
  const enriched: TimesheetRow[] = completeRows
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map((row) => {
      const dateKey = toDateString(row.date);
      const entry = entriesByDate[dateKey];
      const adjustment = adjustmentsByDate[dateKey];
      const dayAllocations = allocationsByDate[dateKey] ?? [];
      return {
        ...row,
        clockIn: entry?.clockIn ?? null,
        breakStart: entry?.breakStart ?? null,
        breakEnd: entry?.breakEnd ?? null,
        clockOut: entry?.clockOut ?? null,
        adjustment,
        dayAllocations,
        totalLaunched: dayAllocations.reduce((sum, a) => sum + a.hours, 0),
      };
    });

  const totals = (timesheet ?? []).reduce(
    (acc, row: Timesheet) => ({
      worked: acc.worked + row.hoursWorked,
      expected: acc.expected + row.expectedHours,
      balance: acc.balance + row.hourBalance,
    }),
    { worked: 0, expected: 0, balance: 0 }
  );

  const employeeOptions = [
    { value: '', label: 'Selecione um colaborador' },
    ...(employees ?? [])
      .filter((e) => e.user?.role === 'COLLABORATOR')
      .map((e) => ({
        value: e.id,
        label: e.user?.name ?? e.id,
      })),
  ];

  // Linha e data do modal de horas
  const hoursRowDate = hoursRow
    ? toDateString(hoursRow.date)
    : '';
  const hoursRowAllocations = hoursRow
    ? (allocationsByDate[hoursRowDate] ?? [])
    : [];

  return (
    <>
      {adjustingRow && (
        <AdjustmentModal
          row={adjustingRow}
          entry={entriesByDate[toDateString(adjustingRow.date)]}
          onClose={() => setAdjustingRow(null)}
        />
      )}

      {hoursRow && (
        <HoursModal
          row={hoursRow}
          allocations={hoursRowAllocations}
          projects={projects ?? []}
          dateStr={hoursRowDate}
          employeeId={effectiveEmployeeId}
          onClose={() => setHoursRow(null)}
        />
      )}

      <Card>
        {/* Header do card */}
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b">

          {/* Lado esquerdo: select de colaborador */}
          <div className="flex-1 min-w-0">
            {canSelectEmployee ? (
              <Select
                value={selectedEmployeeId ?? ''}
                onChange={(e) => setSelectedEmployeeId(e.target.value || undefined)}
                options={employeeOptions}
                className="max-w-[220px]"
              />
            ) : (
              <div />
            )}
          </div>

          {/* Lado direito: navegação de mês */}
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={onPrevMonth}
              className="p-1.5 rounded hover:bg-accent transition-colors"
              aria-label="Mês anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-medium min-w-[130px] text-center capitalize">{monthLabel}</span>
            <button
              onClick={onNextMonth}
              className="p-1.5 rounded hover:bg-accent transition-colors"
              aria-label="Próximo mês"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

        </div>

        <CardContent className="pt-4">
          {isLoading ? (
            <div className="space-y-2 animate-pulse">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-10 bg-muted rounded" />
              ))}
            </div>
          ) : !timesheet || timesheet.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Nenhum registro encontrado para este período.
            </p>
          ) : (
            <div className="space-y-4">
              {/* Totais no topo */}
              <TotalsBar worked={totals.worked} expected={totals.expected} balance={totals.balance} />

              {/* Tabela com ordenação */}
              <SortableTable<TimesheetRow>
                data={enriched}
                defaultSortKey="date"
                defaultSortDirection="asc"
              >
                {(sorted) => (
                  <Table>
                    <TableHeader>
                      <tr className="border-b">
                        <TableHead sortable sortKey="date">Data</TableHead>
                        <TableHead sortable sortKey="clockIn" align="center">Entrada</TableHead>
                        <TableHead sortable sortKey="breakStart" align="center">Saída Almoço</TableHead>
                        <TableHead sortable sortKey="breakEnd" align="center">Volta Almoço</TableHead>
                        <TableHead sortable sortKey="clockOut" align="center">Saída</TableHead>
                        <TableHead sortable sortKey="hoursWorked" align="center">Trabalhado</TableHead>
                        <TableHead sortable sortKey="hourBalance" align="center">Saldo</TableHead>
                        <TableHead sortable sortKey="accumulatedBalance" align="center">Saldo Acum.</TableHead>
                        <TableHead align="center">{'Status'}</TableHead>
                        <TableHead align="right">{''}</TableHead>
                      </tr>
                    </TableHeader>
                    <TableBody>
                      {sorted.map((row) => {
                        const adj = row.adjustment;
                        const isApproved = adj?.status === 'APPROVED';

                        return (
                          <TableRow key={row.id} className={cn(isApproved && 'bg-green-50/40 dark:bg-green-950/10')}>
                            {/* Data */}
                            <TableCell className="text-muted-foreground whitespace-nowrap text-sm">
                              {formatDateShort(row.date)}
                            </TableCell>

                            {/* Entrada */}
                            <TableCell className="text-center">
                              <span className="font-mono text-sm">
                                {row.clockIn ? formatTimeBRT(row.clockIn) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </span>
                            </TableCell>

                            {/* Saída Almoço */}
                            <TableCell className="text-center">
                              <span className="font-mono text-sm">
                                {row.breakStart ? formatTimeBRT(row.breakStart) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </span>
                            </TableCell>

                            {/* Volta Almoço */}
                            <TableCell className="text-center">
                              <span className="font-mono text-sm">
                                {row.breakEnd ? formatTimeBRT(row.breakEnd) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </span>
                            </TableCell>

                            {/* Saída */}
                            <TableCell className="text-center">
                              <span className="font-mono text-sm">
                                {row.clockOut ? formatTimeBRT(row.clockOut) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </span>
                            </TableCell>

                            {/* Trabalhado */}
                            <TableCell className="text-center">
                              <span className="font-mono text-sm text-amber-600 dark:text-amber-400 font-semibold">
                                {formatHours(row.hoursWorked)}
                              </span>
                            </TableCell>

                            {/* Saldo */}
                            <TableCell className="text-center">
                              <BalanceBadge hours={row.hourBalance} />
                            </TableCell>

                            {/* Saldo Acumulado */}
                            <TableCell className="text-center">
                              <span className="font-mono text-sm text-muted-foreground">
                                {row.accumulatedBalance >= 0 ? '+' : ''}{formatHours(row.accumulatedBalance)}
                              </span>
                            </TableCell>

                            {/* Status (ajuste + horas) */}
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center gap-1">
                                {adj && <AdjustmentStatusBadge status={adj.status} />}
                                <HoursBadge
                                  launched={row.totalLaunched}
                                  expected={row.hoursWorked}
                                />
                              </div>
                            </TableCell>

                            {/* Ações */}
                            <TableCell className="text-right pr-3">
                              <div className="flex items-center justify-end gap-1.5">
                                {/* Botão lançar horas */}
                                <button
                                  onClick={() => setHoursRow(row)}
                                  title="Lançar horas"
                                  className={cn(
                                    'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium',
                                    'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600',
                                    'transition-colors'
                                  )}
                                >
                                  <Briefcase className="h-3.5 w-3.5" />
                                  Horas
                                </button>

                                {/* Botão ajustar ponto */}
                                <button
                                  onClick={() => setAdjustingRow(row)}
                                  title="Solicitar ajuste de ponto"
                                  className={cn(
                                    'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium',
                                    'bg-red-600 text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600',
                                    'transition-colors'
                                  )}
                                >
                                  <AlertCircle className="h-3.5 w-3.5" />
                                  Ajustar
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </SortableTable>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
